import React, { useRef } from 'react';
import { Container } from 'react-bootstrap';
import { motion, useInView, useReducedMotion } from 'framer-motion';
import ScrollReveal from '../ScrollReveal';

const missionItems = [
    'Deliver high-quality, reliable, and tailored services that fit each client’s specific goals and way of working.',
    'Continuously innovate and adapt to the evolving technology landscape, staying ahead of the curve without adding unnecessary complexity.',
    'Build long-term partnerships based on integrity, transparency, and mutual respect, so clients know they can depend on us.',
    'Empower clients with clear guidance, honest feedback, and ongoing support, not just one-off project delivery.',
    'Maintain a positive, collaborative attitude – bringing good energy to every engagement while keeping the focus on outcomes.',
    'Do work we are proud of, so that our clients can be proud of the solutions they offer to their own customers.',
];

const missionStagger = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.13 } },
};

const missionItemVariants = {
    hidden: { opacity: 0, x: -30 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.55, ease: 'easeOut' } },
};

function VisionMission() {
    const shouldReduceMotion = useReducedMotion();
    const quoteRef = useRef(null);
    const quoteInView = useInView(quoteRef, { once: true, margin: '-80px' });
    const dividerRef = useRef(null);
    const dividerInView = useInView(dividerRef, { once: true, margin: '-50px' });

    return (
        <motion.div
            initial={shouldReduceMotion ? false : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={shouldReduceMotion ? {} : { opacity: 0, y: -10 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
        >
            <Container fluid className="limited-width bg ogs-pri-text text-center py-5 mb-5">
                <h1 className="ogs-sec-text">Vision & Mission</h1>
            </Container>

            <Container fluid className="limited-width bg ogs-pri-text py-5 mb-5">
                <motion.div
                    ref={quoteRef}
                    className="ogs-quote-card"
                    initial={shouldReduceMotion ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.97 }}
                    animate={shouldReduceMotion || quoteInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.97 }}
                    transition={{ duration: shouldReduceMotion ? 0 : 0.7, ease: 'easeOut' }}
                >
                    <h4 className="ogs-sec-text text-center mb-4 mt-4">Our Vision 👁️‍🗨️</h4>
                    <p>
                        To be a trusted <b className="ogs-high-text">multi-service partner</b> that empowers businesses and individuals with reliable technology and thoughtful solutions,
                        enabling sustainable growth, smarter operations, and better experiences for the people who rely on them.
                    </p>
                </motion.div>

                <motion.div
                    ref={dividerRef}
                    className="ogs-divider"
                    initial={{ width: shouldReduceMotion ? '100%' : 0 }}
                    animate={{ width: shouldReduceMotion || dividerInView ? '100%' : 0 }}
                    transition={{ duration: shouldReduceMotion ? 0 : 1.1, ease: 'easeOut' }}
                />

                <h4 className="ogs-sec-text text-center mb-4 mt-5">Our Mission 🎯</h4>
                <motion.div
                    variants={shouldReduceMotion ? {} : missionStagger}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: '-80px' }}
                >
                    {missionItems.map((text, i) => (
                        <motion.div className="ogs-mission-item" variants={shouldReduceMotion ? {} : missionItemVariants} key={text}>
                            <div className="ogs-mission-number">{i + 1}</div>
                            <div className="ogs-mission-text">
                                {text}
                                <div className="ogs-mission-underline" style={{ '--underline-delay': `${0.4 + i * 0.1}s` }} />
                            </div>
                        </motion.div>
                    ))}
                </motion.div>

                <ScrollReveal>
                    <p>
                        At OG Services, <b className="ogs-high-text">success</b> is measured not only by working software, but by how well it supports your people, your customers, and your long-term strategy.
                        Our goal is to make technology feel like an <b className="ogs-high-text">asset</b>, not a hurdle.
                    </p>
                </ScrollReveal>
            </Container>
        </motion.div>
    );
}

export default VisionMission;
